import { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  Box,
  Container,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import {
  HeaderWithButtons,
  Searchbar,
  SortButtonAndMenu,
} from "../components-molecules";
import { IReferralCode, ReferralCodeList } from "../components-organisms/ReferralCodeList";

import {
  clearReferralCodes,
  getReferralCodes,
  IListReferralCodesRequestFilter,
} from "../redux/actions/referralCodes";
import {
  referralCodefilterOptions as defaultFilters,
  ISortItem,
  referralCodesSortTypes,
} from "../utils/promo-code/headerOptions";
import { OrderSortType } from "../utils/promo-code/groupHeaders";
import { FilterButtonWithTitle } from "../components-molecules/FilterButtonWithTitle";
import { buttonHoverAndActive, textButtonHoverAndActive } from "../utils/styles/buttonStyles";
import { DeactivateCodeModal } from "./DeactivateCodeModal";
import { deactivatePromotionCode, editPromotionCode } from "../redux/actions/promotionCodes";
const timeoutDuration = 1500;

export default function ReferralCodes(props: any) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [page, setPage] = useState<number>(1);

  const [sortType, setSortType] = useState<OrderSortType>("Name");
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [filterOptions] = useState(defaultFilters);
  const [filter, setFilter] = useState<IListReferralCodesRequestFilter>({
    DiscountType: [],
    UseType: [],
  });
  const [query, setQuery] = useState<string>("");
  const [currentReferralCode, setCurrentReferralCode] = useState<string>();
  const [codes]: [any] = useSelector((state: any) => [
    state.ReferralCodesSymbolsReducer.data as any,
  ]);

  const getReferralCodesInternal = useCallback(() => {
    dispatch(
      getReferralCodes({
        Size: 15,
        Page: page,
        Query: query,
        OrderBy: sortType,
        Filter: filter,
      })
    );
  }, [dispatch, query, sortType, filter, page]);

  useEffect(() => {
    getReferralCodesInternal();
  }, [query, getReferralCodesInternal]);

  const loadMore = () => {
    setPage(page + 1);
  };

  const onFilterSubmit = (thisFilter: any): void => {
    let tempFilter: IListReferralCodesRequestFilter = {
      DiscountType: thisFilter["DiscountType"] as string[],
      UseType: thisFilter["UseType"] as string[],
    };

    if (thisFilter["Status"]?.length === 1) {
      tempFilter["Status"] = thisFilter["Status"][0] as boolean;
    }

    dispatch(clearReferralCodes());
    setPage(1);
    setFilter(tempFilter as IListReferralCodesRequestFilter);
  };

  const getLoadedCount = () => {
    return codes?.Data?.reduce((p: number, c) => p + c.ReferralCodes?.length, 0);
  };

  const onReactivate = (item: IReferralCode) => {
    const created = new Date(item.Created);
    const expires = new Date(created);
    expires.setFullYear(created.getFullYear() + 40);
    dispatch(
      editPromotionCode(
        item.Id,
        {
          Expires: new Date(expires).toDateString(),
        },
        () => clearData()
      )
    );
  }

  const clearData = () => {
    dispatch(clearReferralCodes());
    getReferralCodesInternal();
    setCurrentReferralCode(undefined);
  }

  return (
    <Box className={classes.container}>
      <CssBaseline />
      <Container className={classes.root}>
        <CssBaseline />
        <HeaderWithButtons
          fullWidthTitle
          title=""
        >
          <HeaderWithButtons.ButtonArea>
            <div className={classes.buttonSet}>
              <SortButtonAndMenu
                data-automation="sort-button"
                menuItems={referralCodesSortTypes.map((sortItem: ISortItem) => ({
                  title: sortItem.title,
                  value: sortItem.value,
                  handleSelect: () => {
                    if (sortItem.value === sortType) return;
                    dispatch(clearReferralCodes());
                    setPage(1);
                    setSortType(sortItem.value);
                  },
                }))}
                selectedSortType={sortType}
              />
              <FilterButtonWithTitle
                defaultOption="DiscountType"
                data-automation="filter-button"
                onSubmit={onFilterSubmit}
                inButtonSet
                options={filterOptions}
                filter={filter}
              />
              <IconButton
                data-automation="search-button"
                className={classes.searchIconButton}
                color="primary"
                onClick={() => setSearchOpen(!searchOpen)}
              >
                <SearchRoundedIcon
                  color="primary"
                  className={classes.searchIcon}
                />
              </IconButton>
            </div>
          </HeaderWithButtons.ButtonArea>
        </HeaderWithButtons>
        <Searchbar
          query={query}
          isOpen={searchOpen}
          placeholder="Search Referral Codes"
          onCancel={() => {
            if (query.length > 0) {
              dispatch(clearReferralCodes());
            }
            setSearchOpen(false);
            setQuery("");
          }}
          onChange={(event) => {
            dispatch(clearReferralCodes());
            debounce((e) => setQuery(e.target.value), timeoutDuration)(event);
          }}
        />
        <ReferralCodeList
          query={query}
          filter={filter}
          sortType={sortType}
          loadedCount={getLoadedCount()}
          loadMore={loadMore}
          onDeactivate={(id) => {
            setCurrentReferralCode(id);
          }}
          onReactivate={onReactivate}
        />
      </Container>
      {currentReferralCode && currentReferralCode.length > 0 ?
        <DeactivateCodeModal
          open={true}
          onClose={() => { }}
          onDeactivate={() => {
            dispatch(
              deactivatePromotionCode(
                currentReferralCode,
                () => clearData()
              )
            );
          }} />
        : null}
    </Box>
  );
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    maxWidth: "840px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  buttonSet: {
    display: "flex",
    flexDirection: "row",
    paddingRight: "24px",
  },
  primaryButton: {
    marginLeft: 8,
    borderRadius: 8,
    ...buttonHoverAndActive
  },
  searchIcon: {
    width: 24,
    height: 24,
  },
  searchIconButton: {
    width: 40,
    height: 40,
    marginLeft: 8,
    ...textButtonHoverAndActive
  },
});
